import { DEALCARD_BASE_URL } from '@/constants';
import { type ConvertedServerSideGetRowFilterModel, type ServerSideGetRowsRequest } from '@/lib/ssrm';
import { type GenericPage, type PagedParams } from '@/types';
import {
  type CompanyUpdateRequest,
  type CompanyViewModel,
  type DuplicateCoverageResult,
} from '@/types/dealcard/company';
import { type DownloadRequest } from '@/types/dealcard/deal';
import { type PersonViewModel } from '@/types/dealcard/person';
import { GenericService } from './GenericService';

export interface CompanyPagedParams extends PagedParams {
  filter?: string;
  capabilities?: string[];
}

export interface CompaniesParams {
  filter?: string;
  pageNumber?: number;
  pageSize?: number;
}

// Note: UpdateCompanyRequest is now imported from types
// Keeping this for backwards compatibility
export interface UpdateCompanyRequest {
  capabilities?: string[] | null;
  name: string;
  salesPersonIds?: string[] | null;
  properties?: { [key: string]: any };
  website?: string;
  serviceTypes?: string[] | null;
}

export interface CompaniesSSRMResponse {
  data: CompanyViewModel[];
  totalCount: number;
}
export class CompanyService extends GenericService<CompanyViewModel, CompanyUpdateRequest, CompanyPagedParams> {
  constructor() {
    super(DEALCARD_BASE_URL, 'companies');
  }

  getCompanies = async (requestParams: CompaniesParams): Promise<GenericPage<CompanyViewModel>> => {
    return await this.get({
      baseUrl: DEALCARD_BASE_URL,
      path: 'companies',
      params: { ...requestParams },
    });
  };

  getSalesPeople = async (companyId: string): Promise<PersonViewModel[]> => {
    return await this.get({
      baseUrl: DEALCARD_BASE_URL,
      path: `companies/${companyId}/salespeople`,
    });
  };

  updateCompany = async (companyId: string, payload: UpdateCompanyRequest): Promise<CompanyViewModel> => {
    return await this.put({
      baseUrl: DEALCARD_BASE_URL,
      path: `companies/${companyId}`,
      body: payload,
    });
  };

  getCompaniesReport = async (request: DownloadRequest): Promise<Blob> => {
    const response = await this.get<Response>({
      baseUrl: DEALCARD_BASE_URL,
      path: 'companies/download',
      params: { ...request },
      contentType: 'application/octet-stream',
    });

    if (response instanceof Response) {
      return response.blob();
    }

    throw new Error('Expected Response object for download');
  };

  getCompaniesSSRM = async (params: ServerSideGetRowsRequest): Promise<CompaniesSSRMResponse> => {
    // Convert filterModel (AG-Grid object format) to convertedFilterModel (backend array format)
    const convertedFilterModel: ConvertedServerSideGetRowFilterModel[] = [];
    if (params.filterModel && typeof params.filterModel === 'object') {
      for (const [fieldName, filterConfig] of Object.entries(params.filterModel)) {
        if (filterConfig && typeof filterConfig === 'object') {
          convertedFilterModel.push({
            name: fieldName,
            filterType: (filterConfig as any).filterType || 'text',
            type: (filterConfig as any).type || 'contains',
            filter: (filterConfig as any).filter || '',
            filterTo: (filterConfig as any).filterTo,
          });
        }
      }
    }

    // Build complete SSRM request with all required fields
    const ssrmRequest = {
      startRow: params.startRow,
      endRow: params.endRow,
      sortModel: params.sortModel || [],
      convertedFilterModel: convertedFilterModel,
      rowGroupCols: params.rowGroupCols || [],
      valueCols: params.valueCols || [],
      pivotCols: params.pivotCols || [],
      pivotMode: params.pivotMode || false,
      groupKeys: params.groupKeys || [],
    };

    // Base64 encode the request
    const jsonString = JSON.stringify(ssrmRequest);
    const base64Encoded = btoa(jsonString);

    const response = await fetch(`${DEALCARD_BASE_URL}/companies/ssrm`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'ssrm-request': base64Encoded,
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP ${response.status}: ${errorText}`);
    }

    const data = await response.json();

    return {
      data: data.rowData || [],
      totalCount: data.rowCount || 0,
    };
  };

  duplicateCoverage = async (
    sourcePersonId: string,
    targetPersonIds: string[],
    removeSource: boolean,
  ): Promise<DuplicateCoverageResult> => {
    return await this.post({
      baseUrl: DEALCARD_BASE_URL,
      path: 'companies/duplicatecoverage',
      body: {
        sourcePersonId,
        targetPersonIds,
        removeSource,
      },
    });
  };
}
